import { api } from '@/core/http';
import { serviceSingletonGetter } from '@/services/serviceGetter';
import {
  GetBillingInvoicesResponse,
  GetBillingSubscriptionsResponse,
  parseBillingInvoicesResponse,
  parseBillingSubscriptionResponse,
} from './billingTypes';
import { useBillingInvoices } from './useBillingInvoices';
import { useBillingSubscription } from './useBillingSubscription';

export interface BillingService {
  getBillingSubscription: () => Promise<GetBillingSubscriptionsResponse>;
  getBillingInvoices: () => Promise<GetBillingInvoicesResponse>;
  downloadInvoicePdf: (invoiceId: string, filename?: string) => Promise<void>;
  reactivateSubscription: () => Promise<void>;
  updatePaymentMethod: (paymentMethodId: string) => Promise<void>;
  useBillingSubscription: typeof useBillingSubscription;
  useBillingInvoices: typeof useBillingInvoices;
}

export const createBillingService = (): BillingService => {
  const getBillingSubscription =
    async (): Promise<GetBillingSubscriptionsResponse> => {
      const response = await api.get('/billing/subscription');
      return parseBillingSubscriptionResponse(response.data);
    };

  const getBillingInvoices = async (): Promise<GetBillingInvoicesResponse> => {
    const response = await api.get('/billing/invoices');
    return parseBillingInvoicesResponse(response.data);
  };

  const downloadInvoicePdf = async (
    invoiceId: string,
    filename?: string
  ): Promise<void> => {
    const response = await api.get(`/billing/invoices/${invoiceId}/pdf`, {
      responseType: 'blob',
    });
    const blob =
      response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || `invoice-${invoiceId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const reactivateSubscription = async (): Promise<void> => {
    await api.post('/billing/subscription/reactivate');
  };

  const updatePaymentMethod = async (
    paymentMethodId: string
  ): Promise<void> => {
    await api.post('/stripe/payment-methods', {
      paymentMethodId,
    });
  };

  return {
    getBillingSubscription,
    getBillingInvoices,
    downloadInvoicePdf,
    reactivateSubscription,
    updatePaymentMethod,
    useBillingSubscription,
    useBillingInvoices,
  };
};

export default serviceSingletonGetter(
  Symbol('billingService'),
  createBillingService
);
